import React, { useContext, useState } from "react";

import { ApiRoutes, FetchContext, HttpMethods, ToastContext, } from "../../contexts";

import { useAggregatedState } from "../../../helpers";

export const CompetenzaContext = React.createContext(null);

const { Provider } = CompetenzaContext;

export const CompetenzaProvider = (props) => {
    const { showWarningMessage, showSuccessMessage, showErrorMessage } =
        useContext(ToastContext);
    const { fetchOk, fetchAuthorized } = useContext(FetchContext);
    const [state, setState] = useAggregatedState({
        comune: null,
        competenze: { result: [], totalCount: 0 },
        uffici: null,
        ufficiOptions: [],
        tipiUffici: null,
        tipiUfficiOptions: [],
        assignmentUffici: null,
        assignmentUfficiOptions: [],
        comuni: null,
        comuniOptions: [],
        regioni: null,
        regioniOptions: [],
        province: null,
        provinceOptions: [],
    });

    const [isLoadingCompetenza, setIsLoadingCompetenza] = useState(false);
    const getCompetenzaAsync = async (competenzaId) => {
        try {
            setIsLoadingCompetenza(true);
            const r = await fetchAuthorized(
                ApiRoutes.Competenza.GetCompetenza({ competenzaId }),
                {
                    method: HttpMethods.GET,
                }
            );
            const result = await r.json();
            setState({
                competenza: {
                    ...result,
                    inizioValidita: new Date(result.inizioValidita),
                    fineValidita: result.fineValidita
                        ? new Date(result.fineValidita)
                        : null,
                },
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingCompetenza(false);
        }
    };

    const [isUpdatingCompetenza, setIsUpdatingCompetenza] = useState(false);
    const updateCompetenzaAsync = async (competenzaId, competenza) => {
        try {
            setIsUpdatingCompetenza(true);
            const r = await fetchAuthorized(
                ApiRoutes.Competenza.UpdateCompetenza({ competenzaId }),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(competenza),
                }
            );
            const result = await r.json();
            setState({
                competenza: {
                    ...result,
                    inizioValidita: new Date(result.inizioValidita),
                    fineValidita: result.fineValidita
                        ? new Date(result.fineValidita)
                        : null,
                },
            });
            showSuccessMessage("Competenza aggiornata");
            window.close();
        } catch (e) {
            console.debug(e);
        } finally {
            setIsUpdatingCompetenza(false);
        }
    };

    const [isAddingCompetenza, setIsAddingCompetenza] = useState(false);
    const addCompetenzaAsync = async (competenza) => {
        try {
            setIsAddingCompetenza(true);

            const r = await fetchAuthorized(ApiRoutes.Competenza.AddCompetenza(), {
                method: HttpMethods.PUT,
                body: JSON.stringify(competenza),
            });
            const result = await r.json();
            setState({
                competenza: {
                    ...result,
                    inizioValidita: new Date(result.inizioValidita),
                    fineValidita: result.fineValidita
                        ? new Date(result.fineValidita)
                        : null,
                },
            });
            showSuccessMessage("Competenza creata");
            window.close();
        } catch (e) {
            console.debug(e);
        } finally {
            setIsAddingCompetenza(false);
        }
    };

    const [isLoadingTipiUffici, setIsLoadingTipiUffici] = useState(false);
    const getTipiUfficiAsync = async (pageIndex = 0, pageSize = 100) => {
        try {
            setIsLoadingTipiUffici(true);
            const r = await fetchAuthorized(
                ApiRoutes.TipoUfficio.GetTipiUffici({ pageIndex, pageSize }),
                {
                    method: HttpMethods.GET,
                }
            );
            const list = await r.json();
            list.result.forEach(x => x.dataCreazione = new Date(x.dataCreazione));
            setState({
                tipiUffici: list,
                tipiUfficiOptions: list.result
                    .filter(x => x.padreId == 1)
                    .map((e) => {
                        return { tipoUfficioId: e.id, label: `${e.nome}` };
                    }),
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingTipiUffici(false);
        }
    };

    const [isLoadingUffici, setIsLoadingUffici] = useState(false);
    const getUfficiAsync = async (tipiIds = null, pageIndex = 0, pageSize = 1000) => {
        try {
            setIsLoadingUffici(true);
            const r = await fetchAuthorized(
                ApiRoutes.Ufficio.GetUffici(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify({ pageIndex, pageSize, tipiIds: tipiIds, }),
                }
            );
            const list = await r.json();
            setState({
                uffici: list,
                ufficiOptions: list.result
                    .map((e) => {
                        return { ufficioId: e.id, label: `${e.nome}` };
                    })
                    .sort((a, b) => a.label.localeCompare(b.label)),
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingUffici(false);
        }
    };

    const [isLoadingAssignmentUffici, setIsLoadingAssignmentUffici] =
        useState(false);
    const getAssignmentUfficiAsync = async (
        term = null,
        pageIndex = 0,
        pageSize = 20
    ) => {
        try {
            setIsLoadingAssignmentUffici(true);
            const r = await fetchAuthorized(
                ApiRoutes.Ufficio.GetUffici(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify({ pageIndex, pageSize, term, }),
                }
            );
            const list = await r.json();
            setState({
                assignmentUffici: list,
                assignmentUfficiOptions: list.result
                    .map((e) => {
                        return { ufficioId: e.id, label: `${e.nome}` };
                    })
                    .sort((a, b) => a.label.localeCompare(b.label)),
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingAssignmentUffici(false);
        }
    };

    const [isLoadingComuni, setIsLoadingComuni] = useState(false);
    const getComuniAsync = async (term = null, pageSize = 100, pageIndex = 0) => {
        try {
            setIsLoadingComuni(true);
            const r = await fetchAuthorized(
                ApiRoutes.Comune.GetComuni({ term, pageIndex, pageSize }),
                {
                    method: HttpMethods.GET,
                }
            );
            const list = await r.json();
            list.result.forEach((x) => {
                x.dataIstituzione = new Date(x.dataIstituzione);
                x.dataCessazione = x.dataCessazione ? new Date(x.dataCessazione) : null;
            });
            setState({
                comuni: list,
                comuniOptions: list.result
                    .map((e) => {
                        return {
                            comuneId: e.id,
                            label: `${e.denominazioneIt} (${e.dataIstituzione
                                ? `${e.dataIstituzione.getDate()}-${e.dataIstituzione.getMonth() + 1
                                }-${e.dataIstituzione.getFullYear()}`
                                : ""
                                }${e.dataCessazione
                                    ? ` / ${e.dataCessazione.getDate()}-${e.dataCessazione.getMonth() + 1
                                    }-${e.dataCessazione.getFullYear()}`
                                    : ""
                                })`,
                        };
                    })
                    .sort((a, b) => a.label.localeCompare(b.label)),
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingComuni(false);
        }
    };

    const [isLoadingRegioni, setIsLoadingRegioni] = useState(false);
    const getRegioniAsync = async (
        term = null,
        pageIndex = 0,
        pageSize = 1000
    ) => {
        try {
            setIsLoadingRegioni(true);
            const r = await fetchAuthorized(
                ApiRoutes.Regione.GetRegioni({ term, pageIndex, pageSize }),
                {
                    method: HttpMethods.GET,
                }
            );
            const list = await r.json();
            setState({
                regioni: list,
                regioniOptions: list.result
                    .map((e) => {
                        return { regioneId: e.id, label: `${e.descrizione}` };
                    })
                    .sort((a, b) => a.label.localeCompare(b.label)),
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingRegioni(false);
        }
    };

    const [isLoadingProvince, setIsLoadingProvince] = useState(false);
    const getProvinceAsync = async (
        term = null,
        pageIndex = 0,
        pageSize = 1000,
        regioneId = null
    ) => {
        try {
            setIsLoadingProvince(true);
            const r = await fetchAuthorized(
                ApiRoutes.Provincia.GetProvince({
                    term,
                    pageIndex,
                    pageSize,
                    regioneId,
                }),
                {
                    method: HttpMethods.GET,
                }
            );
            const list = await r.json();
            list.result.forEach((x) => {
                x.inizioValidita = new Date(x.inizioValidita);
                x.fineValidita = x.fineValidita ? new Date(x.fineValidita) : null;
            });
            setState({
                province: list,
                provinceOptions: list.result
                    .map((e) => {
                        return {
                            provinciaId: e.id,
                            label: `${e.descrizione} (${e.sigla}) (${e.inizioValidita
                                ? `${e.inizioValidita.getDate()}-${e.inizioValidita.getMonth() + 1
                                }-${e.inizioValidita.getFullYear()}`
                                : ""
                                }${e.fineValidita
                                    ? ` / ${e.fineValidita.getDate()}-${e.fineValidita.getMonth() + 1
                                    }-${e.fineValidita.getFullYear()}`
                                    : ""
                                })`,
                        };
                    })
                    .sort((a, b) => a.label.localeCompare(b.label)),
            });
        } catch (e) {
            console.debug(e);
        } finally {
            setIsLoadingProvince(false);
        }
    };

    const [isAssigningCompetenze, setIsAssigningCompetenze] = useState(false);
    const assignCompetenzeAsync = async (
        competenzaId,
        ufficioId,
        comuniIds = null,
        regioneId = null,
        provinciaId = null,
        valid = false,
        invalid = false,
        all = false
    ) => {
        try {
            setIsAssigningCompetenze(true);
            const r = await fetchAuthorized(
                ApiRoutes.Competenza.AssignCompetenze({
                    competenzaId,
                    ufficioId,
                    regioneId,
                    provinciaId,
                    valid,
                    invalid,
                    all,
                }),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(comuniIds ?? []),
                }
            );
            const result = await r.json();
            setState({ competenze: result });
            showSuccessMessage("Competenze assegnate");
        } catch (e) {
            console.debug(e);
        } finally {
            setIsAssigningCompetenze(false);
        }
    };

    return (
        <Provider
            value={{
                state,
                setState,

                isLoadingCompetenza,
                getCompetenzaAsync,

                isUpdatingCompetenza,
                updateCompetenzaAsync,

                isAddingCompetenza,
                addCompetenzaAsync,

                isLoadingTipiUffici,
                getTipiUfficiAsync,

                isLoadingUffici,
                getUfficiAsync,

                isLoadingComuni,
                getComuniAsync,

                isLoadingAssignmentUffici,
                getAssignmentUfficiAsync,

                isAssigningCompetenze,
                assignCompetenzeAsync,

                isLoadingRegioni,
                getRegioniAsync,

                isLoadingProvince,
                getProvinceAsync,
            }}
        >
            {props.children}
        </Provider>
    );
};
